import React, { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import { fmtTime } from './creditApi';

const PARTIES = [['client', 'Client'], ['broker', 'Broker'], ['lender', 'Lender'], ['employer', 'Employer'], ['accountant', 'Accountant'], ['solicitor', 'Solicitor'], ['valuation', 'Valuation']];
const LENDERS = ['CBA', 'NAB', 'ANZ', 'Westpac', 'Macquarie', 'ING', 'Bankwest', 'Bluestone Mortgages', 'MA Money', 'Bendigo Bank', 'Pepper', 'Liberty'];

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span><div className="mt-1">{children}</div></label>
);

const Primary = ({ onClick, busy, children, testid, tone = 'dark' }) => (
  <Button onClick={onClick} disabled={busy} data-testid={testid}
    className={cn('rounded-full font-bold', tone === 'dark' ? 'bg-slate-900 hover:bg-slate-700 text-white' : tone === 'lime' ? 'bg-lime-500 hover:bg-lime-400 text-slate-900' : 'bg-white border border-slate-300 text-slate-800 hover:bg-slate-100')}>
    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}{children}
  </Button>
);

export const WaitingForm = ({ onSubmit, busy, defaultParty = 'client' }) => {
  const [party, setParty] = useState(defaultParty);
  const [what, setWhat] = useState('');
  const [chase, setChase] = useState('');
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="waiting-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Waiting on">
          <Select value={party} onValueChange={setParty}><SelectTrigger data-testid="waiting-party"><SelectValue /></SelectTrigger>
            <SelectContent>{PARTIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Next chase"><Input type="date" value={chase} onChange={(e) => setChase(e.target.value)} data-testid="waiting-chase" /></Field>
      </div>
      <Field label="What we need"><Textarea rows={2} value={what} onChange={(e) => setWhat(e.target.value)} placeholder="e.g. 2 most recent payslips, 3 months bank statements" data-testid="waiting-what" /></Field>
      <Primary tone="light" busy={busy} testid="waiting-submit" onClick={() => onSubmit({ party, what, next_chase_at: chase || null })}>Mark waiting — remove from my queue</Primary>
    </div>
  );
};

export const LodgeForm = ({ file, members, onSubmit, busy }) => {
  const [lender, setLender] = useState(file.lender || '');
  const [amount, setAmount] = useState(file.est_loan_amount || '');
  const [owner, setOwner] = useState(file.credit_team_member || '');
  const [sla, setSla] = useState(file.lender_sla_days || '');
  const [exp, setExp] = useState(file.expected_assessment_date || '');
  return (
    <div className="space-y-3" data-testid="lodge-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lender">
          <Select value={lender || 'none'} onValueChange={(v) => setLender(v === 'none' ? '' : v)}><SelectTrigger data-testid="lodge-lender"><SelectValue placeholder="Lender" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Select lender</SelectItem>{[...new Set([...LENDERS, ...(file.lender ? [file.lender] : [])])].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Loan amount"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="lodge-amount" /></Field>
        <Field label="Credit owner">
          <Select value={owner || 'none'} onValueChange={(v) => setOwner(v === 'none' ? '' : v)}><SelectTrigger data-testid="lodge-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Unassigned</SelectItem>{members.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Lender SLA (bus. days, optional)"><Input type="number" step="0.5" value={sla} onChange={(e) => setSla(e.target.value)} placeholder="default" data-testid="lodge-sla" /></Field>
        <Field label="Expected assessment date"><Input type="date" value={exp} onChange={(e) => setExp(e.target.value)} data-testid="lodge-expected" /></Field>
      </div>
      <Primary tone="lime" busy={busy} testid="lodge-submit" onClick={() => onSubmit({ lender, loan_amount: Number(amount) || undefined, credit_owner: owner || undefined, lender_sla_days: sla ? Number(sla) : undefined, expected_assessment_date: exp || undefined })}>
        <Check className="w-4 h-4 mr-1" /> Lodge application {lender ? `with ${lender}` : ''}
      </Primary>
    </div>
  );
};

export const MirReceivedForm = ({ onSubmit, busy, title = 'Record MIR from lender' }) => {
  const [req, setReq] = useState('');
  const [items, setItems] = useState('');
  const [party, setParty] = useState('client');
  const lines = items.split('\n').map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-3" data-testid="mir-received-form">
      <div className="text-sm font-bold text-slate-900">{title}</div>
      <Field label="What the lender asked for (summary)"><Input value={req} onChange={(e) => setReq(e.target.value)} placeholder="e.g. Updated payslips + explanation of BNPL" data-testid="mir-summary" /></Field>
      <Field label="Items to request — one per line (leave blank if we already have everything)"><Textarea rows={3} value={items} onChange={(e) => setItems(e.target.value)} placeholder={'Payslip\nBank statement\nExplanation letter'} data-testid="mir-items" /></Field>
      {lines.length > 0 && (
        <Field label="Waiting on"><Select value={party} onValueChange={setParty}><SelectTrigger data-testid="mir-party"><SelectValue /></SelectTrigger>
          <SelectContent>{PARTIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></Field>
      )}
      <Primary busy={busy} testid="mir-received-submit" onClick={() => onSubmit({ requirements: req, items: lines, waiting_on: party })}>
        {lines.length ? `Record MIR · request ${lines.length} item${lines.length !== 1 ? 's' : ''}` : 'Record MIR · information on hand'}
      </Primary>
    </div>
  );
};

export const MirReviewForm = ({ mir, onSubmit, busy }) => {
  const [items, setItems] = useState('');
  const [party, setParty] = useState('client');
  const lines = items.split('\n').map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-3" data-testid="mir-review-form">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">MIR received {fmtTime(mir?.received_at)}</span>{mir?.requirements}</div>
      <Field label="Items we need from the client / third party — one per line"><Textarea rows={3} value={items} onChange={(e) => setItems(e.target.value)} placeholder={'Payslip\nBank statement'} data-testid="mir-review-items" /></Field>
      {lines.length > 0 && (
        <Field label="Waiting on"><Select value={party} onValueChange={setParty}><SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{PARTIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></Field>
      )}
      <div className="flex flex-wrap gap-2">
        <Primary tone="light" busy={busy} testid="mir-review-request" onClick={() => onSubmit({ items: lines, waiting_on: party, internal_only: false })} >{lines.length ? `Request ${lines.length} item${lines.length !== 1 ? 's' : ''} → Waiting` : 'Reviewed — will respond internally'}</Primary>
        <Primary tone="lime" busy={busy} testid="mir-review-onhand" onClick={() => onSubmit({ items: [], internal_only: true })}>Everything on hand → Ready to return</Primary>
      </div>
    </div>
  );
};

export const MirItems = ({ mir, onItemReceived, onReturn, busy }) => {
  const items = mir?.items || [];
  const outstanding = items.filter((i) => !i.received_at);
  const ready = mir?.all_info_received || (items.length > 0 && outstanding.length === 0);
  return (
    <div className="space-y-3" data-testid="mir-items-panel">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">MIR from {mir?.lender || 'lender'} · received {fmtTime(mir?.received_at)}</div>
      <div className="text-sm text-slate-800">{mir?.requirements}</div>
      {items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`mir-item-${i.id}`}>
              <span className={cn('text-sm flex items-center gap-2', i.received_at ? 'text-emerald-700' : 'text-slate-800')}>
                <span className={cn('w-4 h-4 rounded-full inline-flex items-center justify-center', i.received_at ? 'bg-emerald-500 text-white' : 'border border-slate-300')}>{i.received_at && <Check className="w-3 h-3" />}</span>{i.label}
              </span>
              {i.received_at ? <span className="text-[11px] text-slate-500">{fmtTime(i.received_at)}</span>
                : <button disabled={busy} onClick={() => onItemReceived(i)} className="text-xs font-bold text-slate-800 hover:underline" data-testid={`mir-item-received-${i.id}`}>Mark received</button>}
            </li>
          ))}
        </ul>
      )}
      <div className="text-xs text-slate-600">{items.length} requested · {items.length - outstanding.length} received · <b className={outstanding.length ? 'text-rose-600' : 'text-emerald-700'}>{outstanding.length} outstanding</b>{mir?.ready_at && <> · ready since {fmtTime(mir.ready_at)}</>}</div>
      {ready ? <Primary tone="lime" busy={busy} testid="mir-return-btn" onClick={onReturn}><Check className="w-4 h-4 mr-1" /> Everything ready — Return MIR to {mir?.lender || 'lender'}</Primary>
        : <div className="text-xs text-slate-500">Waiting on {mir?.waiting_on || 'client'} — the return task appears automatically once the last item is received.</div>}
    </div>
  );
};

export const ApprovalForm = ({ onSubmit, busy }) => {
  const [kind, setKind] = useState('conditional');
  const [conds, setConds] = useState('');
  const [amt, setAmt] = useState('');
  const lines = conds.split('\n').map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-3" data-testid="approval-form">
      <div className="text-sm font-bold text-slate-900">Record approval from lender</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="approval-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="conditional">Conditional approval</SelectItem><SelectItem value="formal">Formal / unconditional</SelectItem></SelectContent></Select></Field>
        <Field label="Approved amount (optional)"><Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="approval-amount" /></Field>
      </div>
      {kind === 'conditional' && <Field label="Conditions — one per line"><Textarea rows={3} value={conds} onChange={(e) => setConds(e.target.value)} placeholder={'Valuation report\nSigned contract of sale'} data-testid="approval-conditions" /></Field>}
      <Primary busy={busy} testid="approval-submit" onClick={() => onSubmit({ approval_type: kind, conditions: kind === 'conditional' ? lines : [], approved_amount: amt ? Number(amt) : undefined })}>Record {kind} approval</Primary>
    </div>
  );
};

export const ConditionsPanel = ({ file, onReceived, onReviewed, onReturn, busy }) => {
  const open = (file.approval_conditions || []).filter((c) => !c.cleared_at);
  const allIn = open.length > 0 && open.every((c) => c.received_at);
  return (
    <div className="space-y-3" data-testid="conditions-panel">
      <ul className="space-y-1.5">
        {open.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`cond-${c.id}`}>
            <span className={cn('text-sm flex items-center gap-2', c.received_at ? 'text-emerald-700' : 'text-slate-800')}>
              <span className={cn('w-4 h-4 rounded-full inline-flex items-center justify-center', c.received_at ? 'bg-emerald-500 text-white' : 'border border-slate-300')}>{c.received_at && <Check className="w-3 h-3" />}</span>{c.text}
            </span>
            {!c.received_at && <button disabled={busy} onClick={() => onReceived(c)} className="text-xs font-bold text-slate-800 hover:underline" data-testid={`cond-received-${c.id}`}>Mark received</button>}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {!file.conditions_reviewed_at && open.length > 0 && <Primary tone="light" busy={busy} testid="cond-reviewed-btn" onClick={onReviewed}>Reviewed — waiting on client</Primary>}
        {allIn && <Primary tone="lime" busy={busy} testid="cond-return-btn" onClick={onReturn}><Check className="w-4 h-4 mr-1" /> Return conditions to {file.lender || 'lender'}</Primary>}
      </div>
    </div>
  );
};

export const ManualTaskForm = ({ members, onSubmit, busy }) => {
  const [label, setLabel] = useState('');
  const [owner, setOwner] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [due, setDue] = useState('');
  return (
    <div className="space-y-3" data-testid="manual-task-form">
      <Field label="Task"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Call BDM regarding policy exception" data-testid="manual-label" /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Owner"><Select value={owner || 'none'} onValueChange={(v) => setOwner(v === 'none' ? '' : v)}><SelectTrigger data-testid="manual-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent><SelectItem value="none">File owner</SelectItem>{members.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Priority"><Select value={priority} onValueChange={setPriority}><SelectTrigger data-testid="manual-priority"><SelectValue /></SelectTrigger>
          <SelectContent>{['URGENT', 'HIGH', 'NORMAL'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Due"><Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="manual-due" /></Field>
      </div>
      <Primary busy={busy} testid="manual-submit" onClick={() => label.trim() && onSubmit({ label, owner: owner || undefined, priority, due_at: due ? new Date(due).toISOString() : null })}>Add task</Primary>
    </div>
  );
};

export const NoteForm = ({ onSubmit, busy, label = 'Log lender update', placeholder }) => {
  const [text, setText] = useState('');
  const [exp, setExp] = useState('');
  return (
    <div className="space-y-2" data-testid="note-form">
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder || 'What did the lender say?'} data-testid="note-text" />
      {label === 'Log lender update' && <Field label="New expected assessment date (optional)"><Input type="date" value={exp} onChange={(e) => setExp(e.target.value)} data-testid="note-expected" /></Field>}
      <Primary tone="light" busy={busy} testid="note-submit" onClick={() => onSubmit({ note: text, text, expected_assessment_date: exp || undefined })}>{label}</Primary>
    </div>
  );
};

export { Primary };
